import { useReducer, useState } from 'react';
import { TextField } from '@warp-ds/react/components/textfield';
import { Button } from '@warp-ds/react/components/button';
import { IconPinMarker16 } from '@warp-ds/icons/react/pin-marker-16';
import cn from 'classnames';

import { t } from 'shared/locale/texts';
import { useConfig } from 'shared/config/ConfigContext';
import { storeLastSearchCar } from 'shared/search/common/lastSearch';
import { trackSearchClick } from 'shared/search/common/tracking';

import { buildCarTrackingItem } from '../../../util/tracking';

import { usePickupLocationResolver } from './hooks/usePickupLocationResolver';
import { useCarValidation } from './hooks/useCarValidation';
import { useCarInitialState } from './hooks/useCarInitialState';

import { FrontpageDatePickerWrapper } from '../../../components/warp/frontpageDatePickerWrapper/FrontpageDatePickerWrapper';
import { CustomSelect } from '../common/CustomSelect';
import { SearchPanelWrapper } from '../common/SearchPanelWrapper';
import { carAutocompleteMapper } from '../common/autocomplete/AutocompleteMapper';
import { Autocomplete } from '../common/autocomplete/Autocomplete';
import { TimePicker } from './TimePicker/TimePicker';
import { AgePicker } from './AgePicker';
import { CarSearchHiddenInputs } from './CarSearchHiddenInputs';

import actions, { reducer } from '../../car/carActions';
import { carDefaultState, type CarState } from '../../car/carDefaultState';

import styles from './CarSearch.module.css';
import { useBreakpoints } from '../../../hooks/useBreakpoints';

type CarSearchProps = {
    prefill?: Partial<CarState>;
    unleash?: Record<string, boolean>;
    position: string;
};

export const CarSearch = ({ prefill, unleash, position }: CarSearchProps) => {
    const [pristine, setPristine] = useState(true);
    const [state, dispatch] = useReducer(reducer, carDefaultState);

    const isSmallScreen = useBreakpoints({ breakpoint: 'sm' });
    const { carAutocompleteUrl } = useConfig();

    const { resolvePickupLocation } = usePickupLocationResolver(dispatch);
    const { invalid, validationErrors, anchorErrorText, getInvalidFields } = useCarValidation(state, pristine);

    useCarInitialState(dispatch, prefill);

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
        setPristine(false);

        if (Object.values(getInvalidFields(false)).some(Boolean)) {
            event.preventDefault();
            return;
        }

        trackSearchClick('Search carrental', '/reise/leiebil', 'car_rental', position, buildCarTrackingItem(state), 'Form');
        storeLastSearchCar(state);
    };

    return (
        <SearchPanelWrapper onSubmit={handleSubmit} action="https://leiebil.finn.no/NO/book" method="GET" dataTestId="carSearch">
            <div className={styles['form-header']}>
                <AgePicker value={state.driverAge?.toString() || ''} onChange={(val) => dispatch(actions.setDriverAge(val))} />
                <CustomSelect
                    slim
                    triggerIcon={isSmallScreen ? <IconPinMarker16 /> : undefined}
                    fullscreenTitle="Hvor skal du levere bilen?"
                    data-testid="returnLocationIsSame"
                    value={state.returnLocationIsSame ? 'same' : 'different'}
                    onChange={(e) => dispatch(actions.setReturnLocationIsSame(e === 'same'))}
                    options={[
                        { value: 'same', label: 'Lever samme sted' },
                        { value: 'different', label: 'Annet leveringssted' },
                    ]}
                />
            </div>
            <div className={styles['form-body']}>
                <Autocomplete
                    vertical="car"
                    id="pickupLocation"
                    label={t('car.autocomplete.pickup.label')}
                    placeholder={t('car.autocomplete.pickup.placeholder')}
                    defaultValue={state.pickupLocation}
                    onSelect={(opt) => resolvePickupLocation(opt)}
                    url={carAutocompleteUrl}
                    mapOptions={carAutocompleteMapper}
                    minChars={3}
                    error={validationErrors.pickupLocation}
                />
                {!state.returnLocationIsSame && (
                    <Autocomplete
                        departure={false}
                        vertical="car"
                        id="returnLocation"
                        label={t('car.autocomplete.return.label')}
                        placeholder={t('car.autocomplete.return.placeholder')}
                        defaultValue={state.returnLocation}
                        onSelect={(o) => dispatch(actions.setReturnLocation(o))}
                        url={carAutocompleteUrl}
                        mapOptions={carAutocompleteMapper}
                        minChars={3}
                        error={validationErrors.returnLocation}
                    />
                )}
                <FrontpageDatePickerWrapper
                    useAnchor={{
                        title: 'Når vil du leie bilen?',
                        invalid: [invalid.pickupDate, invalid.returnDate, invalid.pickupTime, invalid.returnTime].some(Boolean),
                        helpText: anchorErrorText || null,
                    }}
                    className={styles.FrontpageDatePicker}
                    startDate={state.pickupDate}
                    endDate={state.returnDate}
                    useRange
                    onDateChange={(newDates) => dispatch(actions.setDates(newDates))}
                    modalFooter={
                        <TimePicker
                            pickupTime={state.pickupTime}
                            returnTime={state.returnTime}
                            pickupDate={state.pickupDate}
                            returnDate={state.returnDate}
                            onPickupTimeChange={(e) => dispatch(actions.setPickupTime(e))}
                            onReturnTimeChange={(e) => dispatch(actions.setReturnTime(e))}
                        />
                    }>
                    <TextField id="pickupDate" label="Hentedato" invalid={invalid.pickupDate} helpText={validationErrors.pickupDate} />
                    <TextField id="returnDate" label="Leveringsdato" invalid={invalid.returnDate} helpText={validationErrors.returnDate} />
                </FrontpageDatePickerWrapper>
                <Button
                    fullWidth
                    primary
                    type="submit"
                    className={cn(styles['search-button'], {
                        [styles['search-button--error']]: invalid && Object.values(invalid).some((v) => v),
                    })}>
                    {t('action.search')}
                </Button>
            </div>
            <CarSearchHiddenInputs
                driverAge={state.driverAge}
                pickupDate={state.pickupDate}
                pickupTime={state.pickupTime}
                returnDate={state.returnDate}
                returnTime={state.returnTime}
                pickupLocationId={state.pickupLocationId}
                pickupLocationLatitude={state.pickupLocationLatitude}
                pickupLocationLongitude={state.pickupLocationLongitude}
                pickupLocation={state.pickupLocation}
                returnLocationIsSame={state.returnLocationIsSame}
                returnLocationId={state.returnLocationId}
                returnLocationLatitude={state.returnLocationLatitude}
                returnLocationLongitude={state.returnLocationLongitude}
                returnLocation={state.returnLocation}
                unleash={unleash}
            />
        </SearchPanelWrapper>
    );
};
